/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Theme,
  Typography,
  Skeleton,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Cancel from '@mui/icons-material/Cancel';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import { v4 as uuidv4 } from 'uuid';
import Link from './Link';
import {
  CompanyLogoSchema,
  CompanySchema,
  ContactSchema,
  OrganizationSchema,
} from '../definitions/schemas';
import OrganizationsStore from '../stores/observables/OrganizationsStore';
import useDebouncedValue from '../lib/hook/useDebounceValue';
import { uniqBy } from '../lib/Array';
import { getExtension, getFileFromInput, getFileImage } from '../lib/File';
import { IMAGE_FORMATS } from '../config/variables';
import SnackbarStore from '../stores/observables/SnackbarStore';
import CloudStorage from '../Services/CloudStorage';
import { RoomAction } from '../enums/RoomAction';
import { useI18n } from '../lib/hook/useI18n';
import CompanyLogoInterface from '../graphql/CompanyLogos/CompanyLogoInterface';

// exclude domains from getting default logo
const excludeDomains = [
  'aol.',
  'comcast.',
  'gmail.',
  'hotmail.',
  'icloud.',
  'live.',
  'msn.',
  'outlook.',
  'yahoo.',
];

const useStyles = makeStyles((theme: Theme) => ({
  clearBit: {
    color: 'var(--divider-color-light)',
    fontStyle: 'italic',
    marginTop: theme.spacing(1),
  },
  clientLogoWrapper: {
    '& .MuiMenu-list': {
      minWidth: theme.spacing(48),
      padding: '0',
    },
    '& .MuiMenu-paper': {
      marginTop: theme.spacing(1),
    },
  },
  divider: {
    margin: theme.spacing(0, 3),
  },
  domainItem: {
    color: 'var(--background-color-gray)',
    textTransform: 'none',
  },
  logo: {
    maxHeight: theme.spacing(5),
    maxWidth: theme.spacing(22),
    objectFit: 'scale-down',
  },
  logoItem: {
    display: 'block',
    height: 'auto',
    width: theme.spacing(3),
  },
  nameItem: {
    flex: '1 1 auto',
    margin: theme.spacing(0, 1),
    overflow: 'hidden',
    'text-overflow': 'ellipsis',
    'white-space': 'nowrap',
  },
  removeClientLogo: {
    cursor: 'pointer',
    fontSize: theme.spacing(2.5),
    position: 'absolute',
    right: theme.spacing(-3.75),
    top: theme.spacing(-1.25),
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    marginLeft: theme.spacing(4),
    minHeight: theme.spacing(3),
  },
  wrapperLogoItem: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(0.7, 1.3),
    width: '100%',
  },
}));

export interface CoBrandedLogoProps {
  clientLogoPreview?: string | undefined;
  clientLogoUri?: string | undefined;
  clientLogoUrl?: string | undefined;
  company?: CompanySchema;
  editable?: boolean;
  onClientLogoChange?: (logo: string | undefined) => void;
  onClientLogoPreviewChange?: (value: string) => void;
  onClientLogoUrlChange?: (url: string | undefined) => void;
  organization?: OrganizationSchema;
  primaryContact?: ContactSchema;
  roomAction?: RoomAction;
}

const CoBrandedLogo: React.FC<CoBrandedLogoProps> = (
  props: CoBrandedLogoProps
): JSX.Element => {
  const {
    clientLogoPreview,
    clientLogoUri,
    clientLogoUrl,
    company,
    editable,
    onClientLogoChange = () => {},
    onClientLogoPreviewChange = () => {},
    onClientLogoUrlChange = () => {},
    organization,
    primaryContact,
    roomAction,
  } = props;

  const classes = useStyles();
  const { t } = useI18n();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logos, setLogos] = useState<CompanyLogoSchema[]>([]);
  const defaultCompanyName =
    company?.name || primaryContact?.company?.name || '';
  const [logoUrl, setLogoUrl] = useState(defaultCompanyName);
  const [loading, setLoading] = useState(false);
  const debouncedLogoUrl = useDebouncedValue(logoUrl, 250);

  const currentOrganization =
    organization ?? OrganizationsStore.getCurrentOrganization();

  useEffect(() => {
    if (
      editable &&
      roomAction === RoomAction.Create &&
      clientLogoUri === undefined &&
      clientLogoUrl === undefined
    ) {
      fetchDefaultClientLogo();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchClientLogos(debouncedLogoUrl);
  }, [debouncedLogoUrl]);

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const fetchDefaultClientLogo = async () => {
    const domain = primaryContact?.email?.split('@')[1];
    const isExcludedDomain = excludeDomains.some((eDomain) =>
      domain?.includes(eDomain)
    );
    if (!domain || isExcludedDomain) {
      return;
    }
    try {
      setLoading(true);

      const companyLogos = await new CompanyLogoInterface().getCompanyLogos({
        search: domain,
      });

      const domainLogo = companyLogos.find((logo) => logo.domain === domain);

      if (domainLogo) {
        onClientLogoUrlChange(domainLogo.logoUrl);
        onClientLogoPreviewChange(domainLogo.logoUrl);
      }
    } catch (e) {
      //
    } finally {
      setLoading(false);
    }
  };

  const fetchClientLogos = async (domain: string) => {
    if (!domain) {
      setLogos([]);
      return;
    }
    try {
      const companyLogos = await new CompanyLogoInterface().getCompanyLogos({
        search: domain,
      });

      const uniqLogos = uniqBy(
        companyLogos,
        (o) => o.domain
      ) as CompanyLogoSchema[];

      setLogos(uniqLogos);
    } catch (e) {
      //
    }
  };

  const onRemoveClientLogo = () => {
    onClientLogoChange('');
    onClientLogoUrlChange('');
    onClientLogoPreviewChange('');
  };

  const onAddClientLogoUrl = (logo: string) => {
    onClientLogoChange('');
    onClientLogoUrlChange(logo);
    onClientLogoPreviewChange(logo);
    handleClose();
  };

  const onLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = getFileFromInput(e);
    if (!file) {
      return;
    }
    getFileImage(file, {
      onSuccess: async (previewLogo: string) => {
        setLoading(true);
        const organizationId = OrganizationsStore.getCurrent();
        try {
          const { uri } = await CloudStorage.uploadImage(
            { organizationId },
            file,
            `rooms/client-logo-${Date.now()}-${uuidv4()}.${getExtension(file)}`,
            undefined,
            IMAGE_FORMATS
          );
          handleClose();
          onClientLogoChange(uri);
          onClientLogoUrlChange('');
          onClientLogoPreviewChange(previewLogo);
          setLoading(false);
        } catch (err) {
          SnackbarStore.show(err instanceof Error ? err.message : String(err));
        }
      },
      supportedFormats: IMAGE_FORMATS,
    });
  };

  const removeBrokenLogo = (removeDomain: string) => {
    const nLogos = logos.filter(({ domain }) => domain !== removeDomain);
    setLogos(nLogos);
  };

  const renderLogo = (): JSX.Element => {
    if (!currentOrganization) {
      return (
        <Box flex="1" justifyContent="center" textAlign="center">
          <Skeleton className={classes.logo} variant="rectangular" />
        </Box>
      );
    }

    if (!currentOrganization.logo) {
      return (
        <Box alignItems="center" display="flex" justifyContent="center">
          <Typography component="h1" variant="h5">
            {currentOrganization.name}
          </Typography>
        </Box>
      );
    }

    return (
      <img
        className={classes.logo}
        src={currentOrganization.logo}
        alt={`${currentOrganization.name} logo`}
      />
    );
  };

  const renderClientLogo = () => (
    <Box position="relative">
      {editable && (
        <Cancel
          className={classes.removeClientLogo}
          onClick={onRemoveClientLogo}
        />
      )}
      <img
        className={classes.logo}
        src={clientLogoPreview || clientLogoUrl || clientLogoUri}
        alt="client logo"
      />
    </Box>
  );

  const renderLogos = () => {
    if (!logos.length) {
      return null;
    }

    return logos.map((logo: CompanyLogoSchema, index: number) => (
      <MenuItem
        key={`${logo.domain}-${index}`}
        onClick={() => onAddClientLogoUrl(logo.logoUrl)}
        disableGutters
      >
        <div className={classes.wrapperLogoItem}>
          <span>
            <img
              alt="client logo"
              className={classes.logoItem}
              onError={() => removeBrokenLogo(logo.domain)}
              // file deepcode ignore DOMXSS: this doesn't come from user input
              src={logo.logoUrl}
            />
          </span>
          <span className={classes.nameItem}>{logo.name}</span>
          <span className={classes.domainItem}>{logo.domain}</span>
        </div>
      </MenuItem>
    ));
  };

  const renderClientLogoSection = () => {
    if (clientLogoPreview || clientLogoUrl || clientLogoUri) {
      return renderClientLogo();
    }

    if (!editable) {
      return null;
    }

    if (loading) {
      return <CircularProgress color="secondary" size={32} />;
    }

    return (
      <>
        <Button color="secondary" onClick={handleClickMenu} variant="outlined">
          Add Client&apos;s logo
        </Button>
        <Menu
          id="logos-menu"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          anchorEl={anchorEl}
          className={classes.clientLogoWrapper}
          keepMounted
          onClose={handleClose}
          open={!!anchorEl}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          variant="menu"
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            p={3}
          >
            <TextField
              color="secondary"
              label={`Enter ${t('organization.company').toLowerCase()} name`}
              onChange={(event) => {
                setLogoUrl(event.target.value);
              }}
              value={logoUrl}
              variant="outlined"
            />
            {renderLogos()}
            <Typography className={classes.clearBit} variant="caption">
              <a href="https://logo.dev" title="Logo API">
                Logos provided by Logo.dev
              </a>
            </Typography>
          </Box>
          <Divider />
          <Box display="flex" justifyContent="end" p={3}>
            <Box component="label" mb="auto">
              <input
                accept={IMAGE_FORMATS.join(',')}
                id="logo-picker"
                onChange={onLogoChange}
                style={{ display: 'none' }}
                type="file"
              />
              <Link
                beforeIcon={<CloudUploadOutlined />}
                text="Upload custom logo"
              />
            </Box>
          </Box>
        </Menu>
      </>
    );
  };

  return (
    <Box className={classes.root}>
      {renderLogo()}
      {(editable || clientLogoPreview || clientLogoUrl || clientLogoUri) && (
        <>
          <Divider
            className={classes.divider}
            flexItem
            orientation="vertical"
          />
          {renderClientLogoSection()}
        </>
      )}
    </Box>
  );
};

export default CoBrandedLogo;
